import { Button } from "@/components/ui/button";
import { Download, ChevronDown, CheckCircle2, Phone, Mail, Globe, MapPin, Building2, Brain, ShieldCheck, Activity, Database, Server, Layers, Cpu, LineChart, Cloud, Zap, ArrowRight, Star, Printer } from "lucide-react";
import { SEO } from "@/components/SEO";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { downloadAsPDF, downloadAsImage } from "@/lib/downloadUtils";

export const HealthcareCapabilityStatement = () => {
    return (
        <div className="min-h-screen bg-slate-50 font-sans print:bg-white text-slate-900">
            <SEO
                title="Healthcare Capability Statement | RSA Tech Group"
                description="Architecting the Intelligent Care Ecosystem. Unified patient data, AI automation, and cloud-native healthcare solutions."
                image="/healthcare-bghero.png"
            />
            {/* Download/Print Control Bar (Hidden in Print) */}
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center print:hidden sticky top-0 z-50 shadow-md">
                <div className="font-bold text-lg">RSA Tech Group Capability Statement - Healthcare</div>
                <div className="flex items-center gap-3">
                    <Button
                        onClick={() => window.print()}
                        variant="outline"
                        className="bg-transparent text-white border-white hover:bg-white/10 font-bold"
                    >
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="bg-white text-slate-900 hover:bg-slate-100 font-bold border-none">
                                <Download className="w-4 h-4 mr-2" />
                                Download
                                <ChevronDown className="w-4 h-4 ml-2" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => downloadAsPDF("capability-statement", "Healthcare_Capability_Statement_RSA_Tech")}>
                                Download as PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadAsImage("capability-statement", "Healthcare_Capability_Statement_RSA_Tech", "png")}>
                                Download as PNG
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadAsImage("capability-statement", "Healthcare_Capability_Statement_RSA_Tech", "jpeg")}>
                                Download as JPG
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* A4 Styled Container - One Page Optimized */}
            <div id="capability-statement" className="max-w-[210mm] h-[297mm] mx-auto bg-white shadow-2xl my-8 print:shadow-none print:my-0 print:w-[210mm] print:h-[297mm] overflow-hidden relative flex flex-col">

                {/* 1. Header (Compact Visual) */}
                <header className="relative h-[160px] shrink-0 overflow-hidden">
                    <img
                        src="/healthcare-bghero.png"
                        alt="Healthcare Technology"
                        className="absolute inset-0 w-full h-full object-cover object-center opacity-95 brightness-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-transparent"></div>

                    <div className="relative z-10 p-6 h-full flex flex-col justify-between">
                        <div className="flex justify-between items-start w-full">
                            <img src="/rsa-logo.png" alt="RSA Tech Group" className="h-12 w-auto object-contain" />

                            <div className="flex flex-col items-end gap-1">
                                <div className="bg-white/90 backdrop-blur-md px-6 py-2 rounded-lg shadow-sm border border-white/50">
                                    <h1 className="text-sm font-extrabold uppercase tracking-widest text-slate-900">Capability Statement</h1>
                                </div>
                                <div className="bg-blue-100/80 backdrop-blur-md px-3 py-1 rounded-full border border-blue-200 shadow-sm">
                                    <p className="text-blue-900 font-extrabold text-[10px] tracking-widest uppercase">Healthcare & Life Sciences</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-auto max-w-xl">
                            <h2 className="text-3xl font-extrabold leading-none text-slate-900 tracking-tighter mb-2 drop-shadow-sm">
                                Architecting the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-600">Intelligent Ecosystem</span>
                            </h2>
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-orange-500"></div>
                                <div className="h-1 w-24 bg-gradient-to-r from-blue-700 to-indigo-600 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* 2. Core Content - Compact */}
                <main className="px-8 py-4 space-y-4 relative z-10 flex-grow">

                    {/* Company Overview Section */}
                    <section className="relative rounded-2xl overflow-hidden p-5 border border-slate-100 shadow-sm">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#f8fbff] via-[#f1f5f9] to-[#eef2ff]"></div>

                        <div className="relative z-10 grid grid-cols-12 gap-6 items-start">
                            <div className="col-span-12 md:col-span-7 space-y-3">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide flex items-center gap-2">
                                    <div className="p-1.5 bg-blue-600 rounded text-white shadow-sm">
                                        <Building2 className="w-3.5 h-3.5" />
                                    </div>
                                    Company Overview
                                </h3>
                                <div className="space-y-2 text-slate-700 text-xs font-medium leading-relaxed text-left">
                                    <p>
                                        RSA Tech Group is a premier Enterprise MSP serving the healthcare sector. We architect intelligent care ecosystems that unify fragmented data, automate administrative burden, and improve clinical outcomes.
                                    </p>
                                    <p>
                                        Operating as a long-term managed solutions partner, we take ownership of your digital health infrastructure, from securing interoperable data layers to deploying predictive clinical intelligence.
                                    </p>
                                </div>
                            </div>

                            {/* Key Differentiators */}
                            <div className="col-span-12 md:col-span-5 relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-200 to-indigo-200 rounded-xl blur opacity-30"></div>
                                <div className="relative bg-white/80 backdrop-blur-xl p-4 rounded-xl border border-white/50 shadow-sm ring-1 ring-blue-50">
                                    <h4 className="font-bold text-slate-900 mb-2 text-xs uppercase tracking-wider border-b border-slate-100 pb-1">Key Differentiators</h4>
                                    <ul className="grid grid-cols-1 gap-1.5">
                                        {[
                                            { icon: CheckCircle2, text: "End-to-End System Ownership" },
                                            { icon: ShieldCheck, text: "HIPAA/FHIR Governance" },
                                            { icon: Layers, text: "Managed Clinical Operations" },
                                            { icon: Brain, text: "AI-Driven Care Delivery" }
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-center gap-2 text-[10px] font-bold text-slate-700">
                                                <div className="w-4 h-4 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100">
                                                    <item.icon className="w-2.5 h-2.5" />
                                                </div>
                                                {item.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Representative Projects Section - 2 Column Grid */}
                    <section className="space-y-2">
                        <div className="flex items-center gap-2 px-1">
                            <div className="p-1 bg-blue-100 rounded text-blue-700"><Server className="w-3.5 h-3.5" /></div>
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Representative Projects</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {/* CASE STUDY 1: DATA UNIFICATION */}
                            <div className="relative rounded-xl p-0.5 bg-gradient-to-br from-blue-200 via-blue-100 to-white shadow-sm overflow-hidden">
                                <div className="bg-white rounded-[10px] p-3 relative z-10 h-full flex flex-col">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="text-[9px] font-bold text-blue-600 uppercase tracking-widest mb-0.5">Integration</div>
                                            <h4 className="text-xs font-bold text-slate-900 leading-tight">Unified Data Platform</h4>
                                        </div>
                                        <div className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-[8px] font-extrabold text-blue-700 uppercase tracking-wider">
                                            Interoperability
                                        </div>
                                    </div>
                                    <div className="space-y-1.5 flex-grow">
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Challenge</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Fragmented patient data across 8 hospitals.</p>
                                        </div>
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Solution</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Unified Data Layer with FHIR engine.</p>
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                        <div className="text-[9px] font-bold text-slate-500">Outcome</div>
                                        <div className="text-lg font-extrabold text-blue-600">40% <span className="text-[9px] font-medium text-slate-600">Faster Verif.</span></div>
                                    </div>
                                </div>
                            </div>

                            {/* CASE STUDY 2: INTELLIGENT OPS */}
                            <div className="relative rounded-xl p-0.5 bg-gradient-to-br from-teal-200 via-teal-100 to-white shadow-sm overflow-hidden">
                                <div className="bg-white rounded-[10px] p-3 relative z-10 h-full flex flex-col">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="text-[9px] font-bold text-teal-600 uppercase tracking-widest mb-0.5">Automation</div>
                                            <h4 className="text-xs font-bold text-slate-900 leading-tight">Intelligent Ops Fabric</h4>
                                        </div>
                                        <div className="px-2 py-0.5 rounded-full bg-teal-50 border border-teal-100 text-[8px] font-extrabold text-teal-700 uppercase tracking-wider">
                                            Operational AI
                                        </div>
                                    </div>
                                    <div className="space-y-1.5 flex-grow">
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Challenge</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Discharge delays & revenue leakage.</p>
                                        </div>
                                        <div>
                                            <div className="text-[8px] uppercase font-bold text-slate-400">Solution</div>
                                            <p className="text-slate-700 text-[10px] leading-tight">Predictive Flow & Smart Staffing.</p>
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                        <div className="text-[9px] font-bold text-slate-500">Outcome</div>
                                        <div className="text-lg font-extrabold text-teal-600">28% <span className="text-[9px] font-medium text-slate-600">Revenue Gain</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* TWO COLUMN - Compact */}
                    <div className="grid grid-cols-2 gap-4 flex-grow">

                        {/* LEFT: Core Services */}
                        <section className="bg-gradient-to-b from-white to-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                                <div className="p-1 bg-blue-100 text-blue-700 rounded"><Layers className="w-3.5 h-3.5" /></div>
                                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-900">Core Services</h3>
                            </div>
                            <div className="space-y-2">
                                {[
                                    { icon: Database, title: "Unified Patient Data", desc: "Single source of truth (FHIR)." },
                                    { icon: Brain, title: "Clinical Intelligence", desc: "NLP for patient insights." },
                                    { icon: Zap, title: "Operational Auto", desc: "Intelligent orchestration." },
                                    { icon: LineChart, title: "Predictive Analytics", desc: "Real-time decision support." },
                                    { icon: Cloud, title: "Secure Cloud-Native", desc: "Scalable & compliant." },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-2.5">
                                        <div className="mt-0.5 w-5 h-5 rounded bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                                            <item.icon className="w-3 h-3" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-[10px] text-slate-900 leading-none mb-0.5">{item.title}</h4>
                                            <p className="text-[9px] text-slate-600 leading-none">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* RIGHT: Why Partner with RSA Tech Group */}
                        <section className="bg-[#0f172a] text-white p-4 rounded-xl border border-slate-800 relative overflow-hidden shadow-sm flex flex-col justify-center">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-[40px] -mr-10 -mt-10"></div>

                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700/50 relative z-10">
                                <div className="p-1 bg-blue-900/50 text-blue-400 rounded ring-1 ring-blue-500/30"><ShieldCheck className="w-3.5 h-3.5" /></div>
                                <h3 className="text-xs font-bold uppercase tracking-wide text-white">Why RSA Tech Group?</h3>
                            </div>

                            <div className="space-y-3 relative z-10">
                                {[
                                    { title: "Specialized CoEs", desc: "Dedicated Accelerators for AI & Cloud." },
                                    { title: "End-to-End Ownership", desc: "Accountability from start to finish." },
                                    { title: "Global Delivery", desc: "Efficient hybrid onshore/offshore." },
                                    { title: "Innovation-Driven", desc: "Future-proofing with AI & Auto." }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-2.5">
                                        <div className="mt-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-blue-400" /></div>
                                        <div>
                                            <h4 className="font-bold text-[10px] text-blue-50 leading-none mb-0.5">{item.title}</h4>
                                            <p className="text-[9px] text-slate-400 leading-tight">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    {/* Contact Footer - Ultra Compact */}
                    <footer className="relative overflow-hidden rounded-xl p-3 shadow-md shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0b24] via-[#16143d] to-[#360c0c]"></div>
                        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-l from-orange-900/20 to-transparent"></div>

                        <div className="relative z-10 flex flex-row justify-between items-center gap-4 text-[10px]">
                            <div className="flex items-center gap-2">
                                <img src="/rsa-logo.png" alt="RSA Tech Group" className="h-6 w-auto brightness-0 invert opacity-100" />
                            </div>

                            <div className="flex items-center gap-4 font-bold tracking-wide text-white/90">
                                <div className="flex items-center gap-1.5">
                                    <Mail className="w-3 h-3 text-orange-400" />
                                    <span>Info@rsatech.com</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Phone className="w-3 h-3 text-orange-400" />
                                    <span>469-927-3307</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Globe className="w-3 h-3 text-orange-400" />
                                    <span>www.rsatech.com</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <MapPin className="w-3 h-3 text-orange-400" />
                                    <span>2601 LittleElm Pkwy, Suite 303, LittleElm, TX 75068</span>
                                </div>
                            </div>
                        </div>
                    </footer>
                </main>

                <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 via-indigo-600 to-orange-600 shrink-0"></div>
            </div>
        </div>
    );
};
